import {
  Component,
  Input,
  SimpleChanges,
  OnChanges,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import currency from "currency.js";
import { FormsModule } from "@angular/forms";
import { Bind } from "primeng/bind";
import { InputText } from "primeng/inputtext";
import { ForceSignPipe } from "../../../shared/form/force-sign.pipe";
import { CurrencyPipe } from "../../../shared/currencies/currency.pipe";
import { CurrencyRoundPipe } from "../../../shared/currencies/currency-round.pipe";

@Component({
  selector: "app-percent-box",
  templateUrl: "./percent-box.component.html",
  styleUrls: ["./percent-box.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    Bind,
    InputText,
    CurrencyPipe,
    ForceSignPipe,
    CurrencyRoundPipe,
  ],
})
export class PercentBoxComponent implements OnChanges {
  public readonly netChange = output<number>();
  public readonly marginChange = output<number>();
  public readonly save = output();

  public readonly editable = input(true);
  public readonly nullable = input(false);
  public readonly displayMargin = input(false);
  public readonly isNetDisabled = input(true);

  @Input() public net: number;
  @Input() public margin: number;
  public total: number;

  private netCache = 0;
  private marginCache = 0;

  private sanitize(value: any): string | number {
    if (typeof value === "string" && value.includes(",")) {
      return value.replace(",", ".");
    }
    return value;
  }

  constructor() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.net) {
      this.net = changes.net.currentValue;
    }
    if (changes.margin) {
      this.margin = changes.margin.currentValue;
    }
    this.calculateTotals();
  }

  public toggleNullableNet() {
    if (!this.isNull()) {
      this.netCache = this.net;
      this.net = null;
      this.margin = null;
    } else {
      this.net = this.netCache;
      this.margin = this.marginCache;
    }
    this.calculateTotals();
    this.marginValueChanged();
    this.netValueChanged();
  }

  public isNull() {
    return this.nullable() && this.net === null;
  }

  public shouldDisplayDash() {
    const displayMargin = this.displayMargin();
    return (
      this.isNull() ||
      (!this.isNull() && !displayMargin && this.net === 0) ||
      (!this.isNull() && displayMargin && this.total === 0)
    );
  }

  public isMarginAndTotalDisabled() {
    return !this.isNetDisabled() && this.net === 0;
  }

  public calculateTotals() {
    this.total = currency(1, { precision: 6 })
      .divide(1 - this.margin / 100)
      .multiply(this.sanitize(this.net)).value;
  }

  public calculateMarginFromTotal() {
    this.margin = currency(this.sanitize(this.net), { precision: 6 })
      .multiply(-1)
      .divide(this.sanitize(this.total))
      .add(1)
      .multiply(100).value;
    this.marginValueChanged();
  }

  public limitMargin() {
    this.margin = Math.min(99, this.margin);
    this.margin = Math.max(0, this.margin);
  }

  public netValueChanged() {
    this.netChange.emit(this.net);
  }

  public marginValueChanged() {
    this.marginChange.emit(this.margin);
  }

  public saveEmit() {
    this.save.emit();
  }
}
